import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { isAuthenticated } from '@/lib/auth';
import { apiClient } from '@/lib/api-client';

interface ApiKey {
  id: string;
  keyPrefix: string;
  label: string;
  isLive: boolean;
  lastUsedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

export default function ApiKeys() {
  const navigate = useNavigate();
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKeyLabel, setNewKeyLabel] = useState('');
  const [newKeyIsLive, setNewKeyIsLive] = useState(false);
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }

    loadApiKeys();
  }, [navigate]);

  const loadApiKeys = async () => {
    try {
      const response = await apiClient.get('/api-keys');
      setApiKeys(response.data.apiKeys);
    } catch (error) {
      console.error('Failed to load API keys:', error);
      setError('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateKey = async () => {
    if (!newKeyLabel.trim()) {
      setError('Please enter a label for the API key');
      return;
    }

    setCreating(true);
    setError(null);

    try {
      const response = await apiClient.post('/api-keys', {
        label: newKeyLabel,
        isLive: newKeyIsLive
      });

      setCreatedKey(response.data.apiKey);
      setNewKeyLabel('');
      setNewKeyIsLive(false);
      await loadApiKeys();
    } catch (error: any) {
      console.error('Failed to create API key:', error);
      setError(error.response?.data?.error || 'Failed to create API key');
    } finally {
      setCreating(false);
    }
  };

  const handleRevokeKey = async (id: string) => {
    if (!confirm('Are you sure you want to revoke this API key? This action cannot be undone.')) {
      return;
    }

    try {
      await apiClient.delete(`/api-keys/${id}`);
      await loadApiKeys();
    } catch (error) {
      console.error('Failed to revoke API key:', error);
      setError('Failed to revoke API key');
    }
  };

  const handleRotateKey = async (id: string) => {
    if (!confirm('Are you sure you want to rotate this API key? The old key will be revoked.')) {
      return;
    }

    try {
      const response = await apiClient.post(`/api-keys/${id}/rotate`);
      setCreatedKey(response.data.apiKey);
      await loadApiKeys();
    } catch (error) {
      console.error('Failed to rotate API key:', error);
      setError('Failed to rotate API key');
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-96">
          <div className="text-gray-600">Loading API keys...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">API Keys</h1>
            <p className="text-gray-600 mt-1">Manage your API keys for integration</p>
          </div>
          <Button onClick={() => setShowCreateModal(true)}>Create API Key</Button>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Created Key Display */}
        {createdKey && (
          <Card className="border-blue-500">
            <CardHeader>
              <CardTitle className="text-blue-700">API Key Created Successfully</CardTitle>
              <CardDescription>
                Make sure to copy your API key now. You won't be able to see it again!
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Input
                  type="text"
                  value={createdKey}
                  readOnly
                  className="font-mono text-sm"
                />
                <Button onClick={() => copyToClipboard(createdKey)} variant="outline">
                  Copy
                </Button>
              </div>
              <Button
                onClick={() => setCreatedKey(null)}
                variant="outline"
                className="mt-4"
              >
                I've saved it securely
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Create Modal */}
        {showCreateModal && (
          <Card className="border-blue-500">
            <CardHeader>
              <CardTitle>Create New API Key</CardTitle>
              <CardDescription>
                Generate a new API key for your integration
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Label
                </label>
                <Input
                  type="text"
                  value={newKeyLabel}
                  onChange={(e) => setNewKeyLabel(e.target.value)}
                  placeholder="e.g., Production API, Development API"
                  disabled={creating}
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isLive"
                  checked={newKeyIsLive}
                  onChange={(e) => setNewKeyIsLive(e.target.checked)}
                  disabled={creating}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                />
                <label htmlFor="isLive" className="text-sm font-medium text-gray-700">
                  Live/Production Key
                </label>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={handleCreateKey}
                  disabled={creating}
                >
                  {creating ? 'Creating...' : 'Create Key'}
                </Button>
                <Button
                  onClick={() => {
                    setShowCreateModal(false);
                    setNewKeyLabel('');
                    setNewKeyIsLive(false);
                    setError(null);
                  }}
                  variant="outline"
                  disabled={creating}
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* API Keys List */}
        <div className="space-y-4">
          {apiKeys.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <div className="text-6xl mb-4">🔑</div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No API Keys Yet</h3>
                <p className="text-gray-600 mb-4">
                  Create your first API key to start integrating with PouchPay
                </p>
                <Button onClick={() => setShowCreateModal(true)}>Create Your First API Key</Button>
              </CardContent>
            </Card>
          ) : (
            apiKeys.map((key) => (
              <Card key={key.id} className={key.revokedAt ? 'opacity-60' : ''}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        {key.label}
                        {key.isLive && (
                          <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded">
                            LIVE
                          </span>
                        )}
                        {!key.isLive && (
                          <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded">
                            TEST
                          </span>
                        )}
                        {key.revokedAt && (
                          <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded">
                            REVOKED
                          </span>
                        )}
                      </CardTitle>
                      <CardDescription className="font-mono mt-1">
                        {key.keyPrefix}••••••••••••••••
                      </CardDescription>
                    </div>
                    {!key.revokedAt && (
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleRotateKey(key.id)}
                          variant="outline"
                          size="sm"
                        >
                          Rotate
                        </Button>
                        <Button
                          onClick={() => handleRevokeKey(key.id)}
                          variant="destructive"
                          size="sm"
                        >
                          Revoke
                        </Button>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Created:</span>{' '}
                      <span className="font-medium">{formatDate(key.createdAt)}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Last Used:</span>{' '}
                      <span className="font-medium">{formatDate(key.lastUsedAt)}</span>
                    </div>
                    {key.revokedAt && (
                      <div className="col-span-2">
                        <span className="text-gray-600">Revoked:</span>{' '}
                        <span className="font-medium text-red-600">{formatDate(key.revokedAt)}</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Info Card */}
        <Card>
          <CardHeader>
            <CardTitle>API Key Best Practices</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-gray-600">
            <p>• Store API keys securely and never commit them to version control</p>
            <p>• Use test keys for development and live keys only in production</p>
            <p>• Rotate keys regularly and immediately if compromised</p>
            <p>• Each application/environment should have its own API key</p>
            <p>• Monitor API key usage in the usage statistics section</p>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
